import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { ProjectList, ProjectQuery } from "@/types/project.model";
import {
  getAllProjectsService,
  getProjectDetailService,
} from "@/service/project.service";

interface ProjectTrackingState {
  projects: ProjectList[];
  filteredProjects: ProjectList[];
  searchTerm: string;
  clientId: string;
  statusFilter: string;
  selectedProject: ProjectList | null;
  loading: boolean;
  noProjects: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  projectType: string;
}

const initialState: ProjectTrackingState = {
  projects: [],
  filteredProjects: [],
  searchTerm: "",
  clientId: "All clients",
  statusFilter: "All statuses",
  selectedProject: null,
  loading: false,
  noProjects: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 20,
  },
  projectType: "billable",
};

// Async thunk for fetching project tracking data
export const fetchProjectTracking = createAsyncThunk(
  "projectTracking/fetchProjects",
  async (
    query: ProjectQuery = { page: 1, pageSize: 20 },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      // Get current filters from state if not provided in query
      const state = getState() as RootState;
      const currentQuery: ProjectQuery = {
        page: query.page || state.projectTracking.pagination.currentPage,
        pageSize: query.pageSize || state.projectTracking.pagination.pageSize,
        search:
          query.search !== undefined
            ? query.search
            : state.projectTracking.searchTerm || undefined,
        status:
          state.projectTracking.statusFilter !== "All statuses"
            ? state.projectTracking.statusFilter.toLowerCase()
            : undefined,
        clientId:
          state.projectTracking.clientId !== "All clients"
            ? state.projectTracking.clientId
            : undefined,
        projectType: state.projectTracking.projectType,
      };

      const {
        data: { data, pagination },
      } = await getAllProjectsService(currentQuery);

      dispatch(setNoProjects(false));

      return {
        projects: data,
        pagination: {
          currentPage: pagination?.page as number,
          totalPages: pagination?.totalPages as number,
          totalItems: pagination?.total as number,
          pageSize: pagination?.pageSize as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoProjects(true));
        throw error;
      }

      dispatch(
        setError(error.message || "Failed to fetch project tracking data")
      );
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const getProjectTrackingDetail = createAsyncThunk(
  "projectTracking/getProjectTrackingDetail",
  async (projectId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const { data } = await getProjectDetailService(projectId);
      // Map API response to our Redux model
      return data.data;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch project detail";
      dispatch(setError(message));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

const projectTrackingSlice = createSlice({
  name: "projectTracking",
  initialState,
  reducers: {
    setNoProjects: (state, action: PayloadAction<boolean>) => {
      state.noProjects = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setClientFilter: (state, action: PayloadAction<string>) => {
      state.clientId = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.statusFilter = action.payload;
    },
    setProjectType: (state, action: PayloadAction<string>) => {
      state.projectType = action.payload;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.clientId = "All clients";
      state.statusFilter = "All statuses";
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.projects;
        state.filteredProjects = action.payload.projects;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProjectTracking.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch project tracking data";
      })
      .addCase(getProjectTrackingDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectTrackingDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProject = action.payload;
      })
      .addCase(getProjectTrackingDetail.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch project tracking detail";
      });
  },
});

export const {
  setNoProjects,
  setLoading,
  setError,
  setSearchTerm,
  setClientFilter,
  setStatusFilter,
  setProjectType,
  resetFilters,
  resetAllState,
} = projectTrackingSlice.actions;

// Selectors
export const selectProjects = (state: RootState) =>
  state.projectTracking.projects;
export const selectFilteredProjects = (state: RootState) =>
  state.projectTracking.filteredProjects;
export const selectSearchTerm = (state: RootState) =>
  state.projectTracking.searchTerm;
export const selectClientFilter = (state: RootState) =>
  state.projectTracking.clientId;
export const selectStatusFilter = (state: RootState) =>
  state.projectTracking.statusFilter;
export const selectProjectType = (state: RootState) =>
  state.projectTracking.projectType;
export const selectProjectTrackingLoading = (state: RootState) =>
  state.projectTracking.loading;
export const selectProjectTrackingError = (state: RootState) =>
  state.projectTracking.error;
export const selectNoProjects = (state: RootState) =>
  state.projectTracking.noProjects;
export const selectPagination = (state: RootState) =>
  state.projectTracking.pagination;
export const selectSelectedProject = (state: RootState) =>
  state.projectTracking.selectedProject;

export default projectTrackingSlice.reducer;
